import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchClientGdComments, postClientGdComment, replyClientGdComment } from "../../services/clientGdService";

const formatDate = (dt) => dt ? new Date(dt).toLocaleString() : "";

const CommentNode = ({ comment, topicId, onReplyPosted, depth = 0 }) => {
  const navigate = useNavigate();
  const isLoggedIn = Boolean(localStorage.getItem("token"));
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [posting, setPosting] = useState(false);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setPosting(true);
    const res = await replyClientGdComment(topicId, comment.id, replyText.trim());
    setPosting(false);
    if (res.status === 200 || res.status === 201) {
      setReplyText("");
      setShowReply(false);
      onReplyPosted();
    } else alert(res.message || "Failed to post reply");
  };

  return (
    <div style={{ marginLeft: depth * 20, borderLeft: depth > 0 ? "2px solid #e8f5e9" : "none", paddingLeft: depth > 0 ? 12 : 0, marginBottom: 10 }}>
      <div style={{ background: depth === 0 ? "#f9f9f9" : "#fff", borderRadius: 8, padding: "10px 14px", border: "1px solid #eee" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <strong style={{ fontSize: 13 }}>{comment.authorName}</strong>
          {comment.authorType === "ADMIN" && (
            <span style={{ fontSize: 11, background: "#e8f5e9", color: "#2e7d32", borderRadius: 999, padding: "1px 8px", fontWeight: 700 }}>Admin</span>
          )}
          <span style={{ fontSize: 11, color: "#bbb" }}>{formatDate(comment.createdAt)}</span>
        </div>
        <p style={{ margin: "0 0 8px", fontSize: 14, color: "#333" }}>{comment.commentText}</p>
        {isLoggedIn ? (
          <button onClick={() => setShowReply((s) => !s)}
            style={{ fontSize: 12, color: "#019095", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            {showReply ? "Cancel" : "Reply"}
          </button>
        ) : (
          <button onClick={() => navigate("/login")}
            style={{ fontSize: 12, color: "#019095", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            Login to reply
          </button>
        )}
        {showReply && (
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <input value={replyText} onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write a reply..."
              style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleReply()} />
            <button onClick={handleReply} disabled={posting}
              style={{ padding: "6px 14px", background: "#019095", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
              {posting ? "..." : "Send"}
            </button>
          </div>
        )}
      </div>
      {comment.replies?.map((r) => (
        <CommentNode key={r.id} comment={r} topicId={topicId} onReplyPosted={onReplyPosted} depth={depth + 1} />
      ))}
    </div>
  );
};

const GdCommentThread = ({ topicId }) => {
  const navigate = useNavigate();
  const isLoggedIn = Boolean(localStorage.getItem("token"));
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchClientGdComments(topicId, { page, size: 20 });
    if (res.status === 200 && res.data) {
      const raw = res.data;
      const list = Array.isArray(raw) ? raw : Array.isArray(raw.content) ? raw.content : Array.isArray(raw.data) ? raw.data : [];
      setComments(list);
      setTotalPages(raw.totalPages || 0);
    }
    setLoading(false);
  }, [topicId, page]);

  useEffect(() => { load(); }, [load]);

  const handlePost = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    const res = await postClientGdComment(topicId, newComment.trim());
    setPosting(false);
    if (res.status === 200 || res.status === 201) { setNewComment(""); setPage(0); load(); }
    else alert(res.message || "Failed to post comment");
  };

  return (
    <div style={{ marginTop: 32 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>💬 Discussion ({comments.length})</h3>

      {/* New comment */}
      {isLoggedIn ? (
        <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
          <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)}
            placeholder="Share your thoughts..."
            style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14, resize: "vertical", minHeight: 72 }} />
          <button onClick={handlePost} disabled={posting}
            style={{ alignSelf: "flex-end", padding: "10px 20px", background: "#019095", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>
            {posting ? "Posting..." : "Post"}
          </button>
        </div>
      ) : (
        <div style={{ marginBottom: 24, padding: "16px 20px", background: "#f0fafa", borderRadius: 10, border: "1px solid #b2dfdb", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 15, color: "#555" }}>Login to join the discussion.</p>
          <button onClick={() => navigate("/login")} style={{ padding: "8px 22px", background: "#019095", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
            Login
          </button>
        </div>
      )}

      {loading ? <p style={{ color: "#888" }}>Loading comments...</p> : comments.length === 0 ? (
        <p style={{ color: "#888" }}>No comments yet. Be the first!</p>
      ) : (
        comments.map((c) => (
          <CommentNode key={c.id} comment={c} topicId={topicId} onReplyPosted={load} />
        ))
      )}

      {totalPages > 1 && (
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <button disabled={page === 0} onClick={() => setPage(page - 1)} style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid #019095", color: "#019095", background: "#fff", cursor: "pointer" }}>← Prev</button>
          <span style={{ lineHeight: "32px", fontSize: 13, color: "#555" }}>Page {page + 1} / {totalPages}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid #019095", color: "#019095", background: "#fff", cursor: "pointer" }}>Next →</button>
        </div>
      )}
    </div>
  );
};

export default GdCommentThread;
